import { Link } from 'react-router-dom';
import { MapPin, Phone, Heart, Shield, Lock } from 'lucide-react';
import { useLanguage, useLp } from '../context/LanguageContext';
import { CookieSettingsButton } from './CookieBanner';

const footerTranslations = {
  it: { privacy: 'Privacy Policy', cookie: 'Cookie Policy', gdpr: 'GDPR Compliant', ssl: 'Connessione Sicura' },
  en: { privacy: 'Privacy Policy', cookie: 'Cookie Policy', gdpr: 'GDPR Compliant', ssl: 'Secure Connection' },
  de: { privacy: 'Datenschutz', cookie: 'Cookie-Richtlinie', gdpr: 'DSGVO Konform', ssl: 'Sichere Verbindung' },
  fr: { privacy: 'Confidentialité', cookie: 'Politique des Cookies', gdpr: 'Conforme RGPD', ssl: 'Connexion Sécurisée' }
};

export const Footer = () => {
  const { t, language } = useLanguage();
  const lp = useLp();
  const ft = footerTranslations[language] || footerTranslations.it;

  const offices = [
    {
      name: t('contact.offices.valeggio.name'),
      address: t('contact.offices.valeggio.address'),
      city: t('contact.offices.valeggio.city'),
      phone: t('contact.offices.valeggio.phone')
    },
    {
      name: t('contact.offices.peschiera.name'),
      address: t('contact.offices.peschiera.address'),
      city: t('contact.offices.peschiera.city'),
      phone: t('contact.offices.peschiera.phone')
    },
    {
      name: t('contact.offices.verona.name'),
      address: t('contact.offices.verona.address'),
      city: t('contact.offices.verona.city'),
      phone: t('contact.offices.verona.phone')
    }
  ];

  const quickLinks = [
    { to: lp('/'), label: t('nav.home') },
    { to: lp('/servizi'), label: t('nav.services') },
    { to: lp('/chi-siamo'), label: t('nav.about') },
    { to: lp('/contatti'), label: t('nav.contact') },
    { to: lp('/faq'), label: t('nav.faq') }
  ];

  return (
    <footer data-testid="footer" className="bg-[#121826] text-white">
      <div className="h-[1px] w-full bg-gradient-to-r from-transparent via-[#C9A227]/30 to-transparent" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand Column */}
          <div className="lg:col-span-1">
            <div className="mb-4">
              <h3 className="font-['Playfair_Display'] text-2xl font-bold italic text-white">
                Accordini Carillo
              </h3>
              <p className="text-sm uppercase tracking-[0.2em] text-slate-300 mt-1">
                Notai Associati
              </p>
            </div>
            <p className="text-slate-300 text-sm leading-relaxed mb-6">
              {t('footer.description')}
            </p>
            <p className="text-slate-400 text-xs">
              {t('footer.vatNumber')}: IT05171120230
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-xs font-bold uppercase tracking-[0.2em] mb-6 text-[#C9A227]">
              {t('footer.quickLinks')}
            </h4>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.to}>
                  <Link
                    to={link.to}
                    data-testid={`footer-link-${link.to.replace('/', '') || 'home'}`}
                    className="inline-block py-1.5 text-white/40 hover:text-[#C9A227] transition-colors text-sm"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contacts */}
          <div className="md:col-span-2 lg:col-span-2">
            <h4 className="text-xs font-bold uppercase tracking-[0.2em] mb-6 text-[#C9A227]">
              {t('footer.contacts')}
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {offices.map((office, index) => (
                <div key={index} className="space-y-2">
                  <h5 className="text-white text-sm font-medium">{office.name}</h5>
                  <div className="flex items-start gap-2 text-slate-400 text-xs">
                    <MapPin className="w-3 h-3 mt-1 flex-shrink-0 text-[#C9A227]" />
                    <span>{office.address}, {office.city}</span>
                  </div>
                </div>
              ))}
            </div>
            {/* Phone numbers aligned on same row */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-4">
              {offices.map((office, index) => (
                <div key={index} className="flex items-center gap-2 text-slate-400 text-xs">
                  <Phone className="w-3 h-3 flex-shrink-0 text-[#C9A227]" />
                  <a href={`tel:${office.phone.replace(/\s/g, '')}`} className="hover:text-white transition-colors">
                    {office.phone}
                  </a>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Bottom Bar with GDPR Links */}
        <div className="mt-16 pt-8 border-t border-white/10">
          {/* GDPR Links Row */}
          <div className="flex flex-wrap justify-center gap-6 mb-6">
            <Link 
              to={lp('/privacy-policy')} 
              data-testid="footer-privacy-link"
              className="text-white/30 hover:text-[#C9A227] transition-colors text-xs"
            >
              {ft.privacy}
            </Link>
            <Link 
              to={lp('/cookie-policy')} 
              data-testid="footer-cookie-link"
              className="text-white/30 hover:text-[#C9A227] transition-colors text-xs"
            >
              {ft.cookie}
            </Link>
            <CookieSettingsButton />
          </div>

          {/* Security Badges Row */}
          <div className="flex flex-wrap justify-center gap-4 mb-8">
            <div className="flex items-center gap-2 px-4 py-2 border border-white/10">
              <Shield className="w-3.5 h-3.5 text-[#C9A227] stroke-[1.5]" />
              <span className="text-white/60 text-[10px] font-bold uppercase tracking-wider">{ft.gdpr}</span>
            </div>
            <div className="flex items-center gap-2 px-4 py-2 border border-white/10">
              <Lock className="w-3.5 h-3.5 text-[#C9A227] stroke-[1.5]" />
              <span className="text-white/60 text-[10px] font-bold uppercase tracking-wider">{ft.ssl}</span>
            </div>
          </div>
          
          {/* Copyright Row */}
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-white/25 text-xs">
              &copy; {new Date().getFullYear()} Studio Notarile Associato Accordini & Carillo
            </p>
            <a
              href="https://telcavoip.ai/it/services/webdesign"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="telcavoip-badge"
              className="flex items-center gap-1.5 text-white/25 hover:text-[#C9A227] transition-colors text-xs"
            >
              <span>{t('footer.madeWith')}</span>
              <Heart className="w-3 h-3 text-red-500 fill-red-500" />
              <span className="font-semibold">TelcaVoIP.AI</span>
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};
